import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  PageIcon,
  PageDescription,
  ContentGrid,
  Card,
  CardTitle,
  CardContent,
  ActionBar,
  Button,
  SearchInput,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  StatusBadge,
  TabContainer,
  TabList,
  Tab,
  TabContent,
  fadeIn
} from '../styles/PageStyles';
import { getModels } from '../services/modelHubService';
import { useNavigate } from 'react-router-dom';
import PageHeaderWithInfo from '../components/common/PageHeaderWithInfo';
import { faChartDiagram } from '@fortawesome/free-solid-svg-icons';

const ModelHub = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('models');
  const [models, setModels] = useState([]);

  useEffect(() => {
    const fetchModelData = async () => {
      const filters = {};
      const sort = {};
      try {
        const response = await getModels(filters, sort);
        setModels(response);
      } catch (error) {
        console.error('Error fetching user data:', error);
        navigate('/login'); // Redirect to login if unauthorized
      }
    };

    fetchModelData();
  }, [navigate]);
  const handleViewModel = (modelId) => {
    navigate(`/model-hub-details/${modelId}`);
  };
  // Filter models based on search term
  const filteredModels = models.filter(model =>
    model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    model.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    model.provider.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <PageContainer>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >

        <PageHeaderWithInfo
          icon={faChartDiagram}
          title="Model Hub"
          description="Central repository for managing and monitoring all AI models used within the application.
          Deploy, version, and track performance metrics for language models, computer vision models, and specialized ML models."
        >
          <div style={{ display: 'flex', gap: '1rem' }}>
            {activeTab === 'models' && (
              <SearchInput
                type="text"
                placeholder="Search models..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            )}

            <Button primary onClick={(e) => {
              handleViewModel(`new`)
            }}>Add New Model</Button>
          </div>
        </PageHeaderWithInfo>

        {/* <TabContainer> */}
          {/* <TabList>
            <Tab active={activeTab === 'models'} onClick={() => setActiveTab('models')}>Models</Tab>
            <Tab active={activeTab === 'performance'} onClick={() => setActiveTab('performance')}>Performance</Tab>
            <Tab active={activeTab === 'costs'} onClick={() => setActiveTab('costs')}>Cost Management</Tab>
          </TabList> */}

          {/* <TabContent active={activeTab === 'models'}> */}
            {/* <ActionBar>
              <SearchInput
                type="text"
                placeholder="Search models..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
               <Button>Filter</Button>
              <Button>Sort</Button> 
            </ActionBar> */}

            {filteredModels.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '3rem',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                margin: '2rem 0'
              }}>
                <img 
                  src="/assets/images/no-results.svg" 
                  alt="No models found" 
                  style={{
                    width: '150px',
                    height: 'auto',
                    marginBottom: '1.5rem',
                    opacity: '0.7'
                  }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNGMEYwRjAiLz48Y2lyY2xlIGN4PSIxMDAiIGN5PSI3MCIgcj0iNDAiIGZpbGw9IiNEMEQwRDAiLz48cmVjdCB4PSI0MCIgeT0iMTIwIiB3aWR0aD0iMTIwIiBoZWlnaHQ9IjQwIiByeD0iOCIgZmlsbD0iI0QwRDBEMCIvPjwvc3ZnPg==";
                  }}
                />
                <h3 style={{ fontSize: '1.5rem', color: 'var(--text-primary)', marginBottom: '0.75rem' }}>
                  No Models Found
                </h3>
                <p style={{ color: 'var(--text-secondary)', maxWidth: '400px', margin: '0 auto' }}>
                  We couldn't find any models matching your search criteria. 
                  Try adjusting your search terms or add a new model.
                </p>
              </div>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <TableHeader>Name</TableHeader>
                    <TableHeader>Type</TableHeader>
                    <TableHeader>Provider</TableHeader>
                    <TableHeader>Version</TableHeader>
                    <TableHeader>Performance</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Actions</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {filteredModels.map(model => (
                    <TableRow key={model.id}>
                      <TableCell>{model.name}</TableCell>
                      <TableCell>{model.type}</TableCell>
                      <TableCell>{model.provider}</TableCell>
                      <TableCell>{model.version}</TableCell>
                      <TableCell>{model.performance}%</TableCell>
                      <TableCell>
                        <StatusBadge status={model.status === 'ACTIVE' ? 'Active' : 'Warning'}>
                          {model.status === 'ACTIVE' ? 'Active' : 'Maintenance'}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <Button onClick={(e) => handleViewModel(model.code)}
                            style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Details</Button>
                          <Button primary style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Configure</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            )}
          {/* </TabContent> */}

{/* 
          <TabContent active={activeTab === 'performance'}>
            <ContentGrid>
              <Card>
                <CardTitle>Model Performance Metrics</CardTitle>
                <CardContent>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
                    {models.map(model => (
                      <div key={model.id}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                          <span>{model.name}</span>
                          <span>{model.performance}%</span>
                        </div>
                        <div style={{ width: '100%', height: '8px', background: 'rgba(0,102,255,0.1)', borderRadius: '4px' }}>
                          <div
                            style={{
                              width: `${model.performance}%`,
                              height: '8px',
                              background: model.performance > 95 ? '#34C759' : model.performance > 90 ? 'var(--primary)' : '#FFCC00',
                              borderRadius: '4px'
                            }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardTitle>Response Latency</CardTitle>
                <CardContent>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
                    {models.map(model => (
                      <div key={model.id}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                          <span>{model.name}</span>
                          <span>{model.latency}</span>
                        </div>
                        <div style={{ width: '100%', height: '8px', background: 'rgba(0,102,255,0.1)', borderRadius: '4px' }}>
                          <div
                            style={{
                              width: `${parseInt(model.latency) / 2}%`,
                              height: '8px',
                              background: parseInt(model.latency) < 100 ? '#34C759' : parseInt(model.latency) < 150 ? '#FFCC00' : '#FF3B30',
                              borderRadius: '4px'
                            }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </ContentGrid>
          </TabContent>

          <TabContent active={activeTab === 'costs'}>
            <Card>
              <CardTitle>Cost Analysis</CardTitle>
              <CardContent>
                <div style={{ marginTop: '1rem' }}>
                  <Table>
                    <thead>
                      <tr>
                        <TableHeader>Model</TableHeader>
                        <TableHeader>Cost Per Token</TableHeader>
                        <TableHeader>Daily Usage</TableHeader>
                        <TableHeader>Daily Cost</TableHeader>
                        <TableHeader>Monthly Projection</TableHeader>
                      </tr>
                    </thead>
                    <tbody>
                      <TableRow>
                        <TableCell>ClaimsGPT-4</TableCell>
                        <TableCell>$0.00012</TableCell>
                        <TableCell>2.5M tokens</TableCell>
                        <TableCell>$300</TableCell>
                        <TableCell>$9,000</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>InsuranceVision</TableCell>
                        <TableCell>$0.00020</TableCell>
                        <TableCell>1.2M tokens</TableCell>
                        <TableCell>$240</TableCell>
                        <TableCell>$7,200</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>FraudDetector</TableCell>
                        <TableCell>$0.00015</TableCell>
                        <TableCell>1.8M tokens</TableCell>
                        <TableCell>$270</TableCell>
                        <TableCell>$8,100</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>RiskAssessor</TableCell>
                        <TableCell>$0.00010</TableCell>
                        <TableCell>1.5M tokens</TableCell>
                        <TableCell>$150</TableCell>
                        <TableCell>$4,500</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>DocExtractor</TableCell>
                        <TableCell>$0.00018</TableCell>
                        <TableCell>1.0M tokens</TableCell>
                        <TableCell>$180</TableCell>
                        <TableCell>$5,400</TableCell>
                      </TableRow>
                    </tbody>
                    <tfoot>
                      <tr>
                        <TableCell colSpan="3" style={{ fontWeight: 'bold' }}>Total</TableCell>
                        <TableCell style={{ fontWeight: 'bold' }}>$1,140</TableCell>
                        <TableCell style={{ fontWeight: 'bold' }}>$34,200</TableCell>
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabContent> */}
        {/* </TabContainer> */}
      </motion.div>
    </PageContainer>
  );
};

export default ModelHub;
